<template>
    <div class="app--field-image" :class="{ 'is-valid': valid === true, 'is-invalid': valid === false }"
        @dragover.prevent="" @dragenter="hover = true" @dragleave="hover = false" @drop="drop">
        <div v-if="empty && hover" class="image-container droppable" :class="defaultClass" :style="defaultStyle">
            <span class="icon" :style="{ 'fontSize': iconSize + 'px' }"><app-icon value="image"></app-icon></span>
            <span class="label"><app-icon value="upload"></app-icon> {{ tDropToUpload }}</span>
        </div>
        <div v-if="empty && !hover" class="image-container empty" :class="defaultClass" :style="defaultStyle"
            v-on:click.prevent="browse">
            <span class="icon" :style="{ 'fontSize': iconSize + 'px' }"><app-icon value="image"></app-icon></span>
            <span class="label"><app-icon value="upload"></app-icon> {{ tUpload }} {{ placeholder }}</span>
        </div>
        <div v-if="!empty" class="image-container" :class="defaultClass" :style="defaultStyle">
            <div class="actions">
                <div class="btn-group" role="group" aria-label="Image options">
                    <button v-if="showRemove" type="button" class="btn"
                        :class="{ 'disabled app--cursor-readonly': !enabled }"
                        :title="$t('app.common.remove') + ' ' + placeholder" @click.prevent="remove"
                        :disabled="!enabled"><app-icon value="remove"></app-icon></button>
                    <button v-if="showEdit" type="button" class="btn" :class="{ 'disabled app--cursor-readonly': !enabled }"
                        :title="$t('app.common.edit') + ' ' + placeholder" @click.prevent="open"
                        :disabled="!enabled"><app-icon value="edit"></app-icon></button>
                </div>
            </div>
            <img :src="file" accept="image/*" />
        </div>
        <input type="file" ref="input" class="form-control" v-on:change="load" v-validate="validate"
            :required="(validateRequired && file == '')" :accept="validateType">
        <div class="invalid-feedback"></div>
        <transition name="overlay">
            <div class="image-edit" v-show="overlay">
                <div class="image-container" ref="cropcontainer"></div>
                <div class="image-actions btn-toolbar justify-content-center" role="toolbar">
                    <div class="btn-group mr-5">
                        <button type="button" class="btn btn-lg btn-dark" v-on:click="close">
                            {{ $t('app.common.cancel') }}
                        </button>
                    </div>
                    <div class="btn-group ml-5">
                        <button type="button" class="btn btn-lg btn-dark" v-on:click="edit">
                            {{ $t('app.common.save_changes') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import Croppie from 'croppie'

export default {
    props: {
        value: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: '',
        },
        enabled: {
            type: Boolean,
            default: true
        },
        width: {
            type: Number,
            default: 1280,
        },
        height: {
            type: Number,
            default: 1280,
        },
        fit: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: 'circle'
        },
        showEdit: {
            type: Boolean,
            default: false
        },
        showRemove: {
            type: Boolean,
            default: true
        },
        defaultClass: {
            type: String,
            default: 'border--element bg--element'
        },
        defaultWidth: {
            type: String,
            default: '100%'
        },
        defaultHeight: {
            type: String,
            default: '98px'
        },
        defaultEditViewport: {
            type: String,
            default: 'square'
        },
        validateRequired: {
            type: Boolean,
            default: false,
        },
        validateSize: {
            type: Number,
            default: 8192
        },
        validateType: {
            type: String,
            default: 'image/gif,image/jpeg,image/pjpeg,image/jpg,image/png,image/x-png'
        },
        tDropToUpload: {
            type: String,
            default: 'Drop to upload'
        },
        tUpload: {
            type: String,
            default: 'Upload'
        }
    },
    emits: ['update:value'],
    data() {
        return {
            iconSize: 48,
            croppie: null,
            file: this.value,
            original: this.value,
            overlay: false,
            valid: null,
            hover: false
        }
    },
    computed: {
        empty: function () {
            return (this.file == '')
        },
        defaultStyle: function () {
            return { 'width': this.defaultWidth, 'height': this.defaultHeight }
        },
        viewport: function () {
            let wWidth = window.innerWidth - 20
            let wHeight = window.innerHeight - 135
            let width = this.width
            let height = this.height

            if (width > wWidth) {
                height = (height * wWidth) / width
                width = wWidth
            }
            if (height > wHeight) {
                width = (width * wHeight) / height
                height = wHeight
            }

            return { width: width, height: height, type: this.defaultEditViewport }
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                this.file = this.original = (this.value === null) ? '' : this.value;
                if (!this.file) {
                    this.remove();
                }
                if (!this.empty) {
                    this.resize(this.file, false)
                }
            }
        },
        original: {
            handler(value) {
                this.$emit('update:value', value)
            }
        }
    },
    mounted() {
        this.$refs.input.addEventListener('invalid', (event) => {
            this.valid = false
        })
        this.$refs.input.addEventListener('valid', (event) => {
            this.valid = true
        })
        this.$refs.input.addEventListener('validation:reset', (event) => {
            this.valid = null
        })
    },
    methods: {
        open: function (event) {
            if (!this.enabled) return
            this.overlay = true
            window.addEventListener('keyup', this.close)
            this.croppie = new Croppie(this.$refs.cropcontainer, {
                enableZoom: true,
                mouseWheelZoom: true,
                showZoomer: true,
                enableExif: true,
                enforceBoundary: false,
                viewport: this.viewport,
                url: this.original
            })
        },
        close: function (event) {
            if (!this.enabled) return
            if (event.type != 'keyup' || event.which == 27) {
                window.removeEventListener('keyup', this.close)
                this.overlay = false
                if (this.croppie != null) {
                    this.croppie.destroy()
                }
            }
        },
        browse: function (event) {
            if (!this.enabled) return
            this.$refs.input.click()
        },
        load: function (event) {
            if (!this.$validate(event)) {
                this.file = ''
                event.target.value = ''
                return
            }
            let reader = new FileReader()
            let file = event.target.files[0]
            reader.onload = (e) => this.resize(e.target.result, true)
            reader.readAsDataURL(file)
        },
        drop(event) {
            event.preventDefault()
            this.hover = false
            if (!this.enabled || !this.empty) return
            if (event.dataTransfer && event.dataTransfer.files.length > 0) {
                let message = this.validateFile(event.dataTransfer.files[0])
                if (message) {
                    this.file = ''
                    return
                }
                let reader = new FileReader()
                let file = event.dataTransfer.files[0]
                reader.onload = (e) => this.resize(e.target.result, true)
                reader.readAsDataURL(file)
            }
        },
        validateFile(file) {
            let message = ''
            if (this.validateType.indexOf(file.type) === -1) {
                message = this.$t('validation.image', { attribute: this.placeholder })
            } else if (this.validateSize > 0 && file.size / 1024 > this.validateSize) {
                message = this.$t('validation.max.file', { attribute: this.placeholder, max: this.validateSize })
            }
            return message
        },
        validate(event) {
            let message = ''
            if (event.target.files.length > 0) {
                message = this.validateFile(event.target.files[0])
            }
            event.target.setCustomValidity(message)
        },
        resize: function (image, updateOriginal) {
            let vm = this
            let canvas = document.createElement("canvas")
            let ctx = canvas.getContext("2d")
            let img = new Image()
            img.onload = function () {
                let posx = 0
                let posy = 0
                let width = img.width
                let height = img.height
                // || (((height * vm.width) / width) - vm.height) / 2 >= 0
                if (width >= height) {
                    height *= vm.width / width
                    width = vm.width
                    posy = 0 - (height - vm.height) / 2
                } else {
                    width *= vm.height / height
                    height = vm.height
                    posx = 0 - (width - vm.width) / 2
                }
                if (vm.fit) {
                    canvas.width = vm.width
                    canvas.height = vm.height
                } else {
                    canvas.width = width
                    canvas.height = height
                    posx = posy = 0
                }
                ctx = canvas.getContext("2d")
                ctx.drawImage(img, posx, posy, width, height)
                vm.file = canvas.toDataURL('image/png')
                if (updateOriginal) {
                    vm.original = vm.file
                }
            }
            img.src = image
        },
        remove: function (event) {
            if (!this.enabled) return
            this.file = ''
            this.original = ''
            if (typeof (this.$refs.input) != 'undefined') {
                this.$refs.input.value = ''
            }
            this.valid = null
        },
        edit: function (event) {
            if (!this.enabled) return
            let vm = this
            this.croppie.result({
                type: 'canvas',
                size: { width: this.width, height: this.height },
                quality: 1,
                circle: false
            }).then(function (result) {
                vm.original = result
                vm.file = result
                vm.close(event)
            })
        }
    }
}
</script>
